/**
 * Copyright 2025 John Huikku Alienrobot LLC
 * www.ALIENROBOT.com
 */

import { useState, useRef, useEffect } from 'react'
import { X, Edit2, Save, Undo } from 'lucide-react'
import { PeerResponse } from '@/core/orchestrator'

interface PeerModalProps {
  peer: {
    id: string
    name: string
    role: string
    color: string
    icon: string
  }
  response: PeerResponse | null
  onClose: () => void
  onSave?: (editedContent: string) => void
}

export function PeerModal({ peer, response, onClose, onSave }: PeerModalProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editedContent, setEditedContent] = useState('')
  const [originalContent, setOriginalContent] = useState('')
  const [undoStack, setUndoStack] = useState<string[]>([])
  const [notes, setNotes] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (response?.content) {
      setOriginalContent(response.content)
      setEditedContent(response.content)
    }
  }, [response])

  const handleEdit = () => {
    setIsEditing(true)
    setUndoStack([editedContent])
  }

  const handleSave = () => {
    setIsEditing(false)
    setOriginalContent(editedContent)
    if (onSave) {
      onSave(editedContent)
    }
  }

  const handleUndo = () => {
    if (undoStack.length > 1) {
      const newStack = [...undoStack]
      newStack.pop()
      setUndoStack(newStack)
      setEditedContent(newStack[newStack.length - 1])
    } else if (undoStack.length === 1) {
      setEditedContent(originalContent)
      setUndoStack([])
    }
  }

  const handleChange = (newContent: string) => {
    setEditedContent(newContent)
    setUndoStack([...undoStack, newContent])
  }

  // Highlight edited text in yellow
  const renderContent = () => {
    if (!isEditing && editedContent !== originalContent) {
      // Split by lines and highlight differences
      const originalLines = originalContent.split('\n')
      const editedLines = editedContent.split('\n')

      return editedLines.map((line, i) => {
        const isChanged = originalLines[i] !== line
        return (
          <div key={i} className={isChanged ? 'bg-yellow-500/20 text-yellow-200' : ''}>
            {line || '\u00A0'}
          </div>
        )
      })
    }

    return <div className="whitespace-pre-wrap">{editedContent}</div>
  }

  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-8"
      onClick={onClose}
    >
      <div
        className="bg-background-secondary border border-border rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border p-4">
          <div className="flex items-center gap-3">
            <div className={`w-3 h-3 rounded-full bg-${peer.color}-500`} />
            <div>
              <h2 className="text-lg font-title">{peer.name}</h2>
              <p className="text-xs text-foreground-muted">{peer.role}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isEditing ? (
              <>
                <button
                  onClick={handleUndo}
                  disabled={undoStack.length === 0}
                  className="p-2 hover:bg-background-tertiary rounded transition-colors disabled:opacity-50"
                  title="Undo"
                >
                  <Undo className="w-4 h-4" />
                </button>
                <button
                  onClick={handleSave}
                  className="p-2 hover:bg-background-tertiary rounded transition-colors text-green-400"
                  title="Save"
                >
                  <Save className="w-4 h-4" />
                </button>
              </>
            ) : (
              <button
                onClick={handleEdit}
                className="p-2 hover:bg-background-tertiary rounded transition-colors"
                title="Edit"
              >
                <Edit2 className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 hover:bg-background-tertiary rounded transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-auto p-6">
          {response ? (
            <>
              {isEditing ? (
                <textarea
                  ref={textareaRef}
                  value={editedContent}
                  onChange={(e) => handleChange(e.target.value)}
                  className="w-full h-[50vh] px-3 py-2 text-sm font-mono leading-relaxed bg-background-tertiary border border-accent rounded resize-none focus:outline-none focus:border-accent"
                  autoFocus
                />
              ) : (
                <div className="text-sm font-mono leading-relaxed">
                  {renderContent()}
                </div>
              )}

              {response.tokens && (
                <div className="text-xs text-foreground-muted pt-4 border-t border-border mt-4">
                  Tokens: {response.tokens.toLocaleString()}
                  {response.cost && ` • Cost: $${response.cost.toFixed(4)}`}
                </div>
              )}
            </>
          ) : (
            <div className="flex items-center justify-center h-full text-foreground-muted">
              <div className="flex items-center gap-2">
                <span className="animate-pulse">●</span>
                <span>Waiting for response...</span>
              </div>
            </div>
          )}
        </div>

        {/* Notes Section - Fixed Footer */}
        {response && (
          <div className="border-t border-border bg-background-secondary p-4 flex-shrink-0">
            <label className="text-xs text-foreground-muted block mb-2">Your Notes / Additional Context:</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add your notes, context, or additional feedback..."
              className="w-full h-24 px-3 py-2 text-sm bg-background-tertiary border border-border rounded resize-none focus:outline-none focus:border-accent"
            />
          </div>
        )}
      </div>
    </div>
  )
}

